import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Edit, Trash2, Gift } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `R$ ${Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const EMPTY = { name: "", items: [{ product_id: "", quantity: "1" }], discount_type: "percent", discount_value: "", active: true, notes: "" };

const ComboCard = ({ c, canManage, onEdit, onDelete }) => (
  <Card data-testid={`combo-card-${c.id}`} className={c.active ? "" : "opacity-60"}>
    <CardContent className="p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-bold text-base flex items-center gap-1"><Gift className="h-4 w-4" />{c.name}</p>
          <div className="flex gap-1 mt-1">
            <Badge className={c.active ? "bg-green-600" : "bg-gray-500"}>{c.active ? "Ativo" : "Inativo"}</Badge>
            <Badge variant="outline">{c.discount_type === "percent" ? `${c.discount_value}% off` : `Preço fixo ${fmt(c.discount_value)}`}</Badge>
          </div>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" disabled={!canManage} onClick={() => onEdit(c)} data-testid={`combo-edit-${c.id}`}><Edit className="h-3 w-3" /></Button>
          <Button size="sm" variant="outline" className="text-red-600" disabled={!canManage} onClick={() => onDelete(c)} data-testid={`combo-delete-${c.id}`}><Trash2 className="h-3 w-3" /></Button>
        </div>
      </div>
      <ul className="text-xs space-y-0.5">
        {c.items.map((it) => <li key={it.product_id}>• {it.quantity} {it.unit || ""} {it.product_name}</li>)}
      </ul>
      <table className="w-full text-xs border-t">
        <tbody>
          {["varejo", "atacado", "especial"].map((pt) => (
            <tr key={pt}>
              <td className="capitalize py-0.5">{pt}</td>
              <td className="text-right line-through text-gray-500">{(c.base_prices?.[pt] || 0) > (c.final_prices?.[pt] || 0) ? fmt(c.base_prices?.[pt]) : ""}</td>
              <td className="text-right font-bold text-emerald-700" data-testid={`combo-price-${c.id}-${pt}`}>{fmt(c.final_prices?.[pt])}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {c.notes && <p className="text-xs italic">{c.notes}</p>}
    </CardContent>
  </Card>
);

const ItemsEditor = ({ items, products, onChange }) => {
  const upd = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-2">
      <label className="text-xs font-bold">Produtos do combo *</label>
      {items.map((it, i) => (
        <div key={i} className="flex gap-2">
          <Select value={it.product_id || "none"} onValueChange={(v) => upd(i, "product_id", v === "none" ? "" : v)}>
            <SelectTrigger className="flex-1" data-testid={`combo-item-product-${i}`}><SelectValue placeholder="Produto" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Selecione —</SelectItem>
              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="number" step="0.01" className="w-24" value={it.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} data-testid={`combo-item-qty-${i}`} />
          <Button size="sm" variant="outline" className="text-red-600" disabled={items.length === 1} onClick={() => onChange(items.filter((_, idx) => idx !== i))}><Trash2 className="h-3 w-3" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => onChange([...items, { product_id: "", quantity: "1" }])} data-testid="combo-add-item"><Plus className="h-3 w-3 mr-1" />Adicionar produto</Button>
    </div>
  );
};

export default function CombosTab({ companyId, products = [], canManage }) {
  const [combos, setCombos] = useState([]);
  const [dlg, setDlg] = useState({ open: false, id: null, data: null });

  const load = () => axios.get(`${API}/companies/${companyId}/factory/combos`).then((r) => setCombos(r.data || []));
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const setData = (patch) => setDlg((d) => ({ ...d, data: { ...d.data, ...patch } }));
  const openEdit = (c) => setDlg({ open: true, id: c.id, data: {
    name: c.name, items: c.items.map((i) => ({ product_id: i.product_id, quantity: String(i.quantity) })),
    discount_type: c.discount_type, discount_value: String(c.discount_value), active: c.active, notes: c.notes || "",
  } });

  const save = async () => {
    const d = dlg.data;
    const items = d.items.filter((i) => i.product_id && parseFloat(i.quantity) > 0).map((i) => ({ product_id: i.product_id, quantity: parseFloat(i.quantity) }));
    if (!d.name.trim() || items.length === 0) { toast.error("Informe o nome e pelo menos um produto"); return; }
    if (!(parseFloat(d.discount_value) > 0)) { toast.error("Informe o desconto"); return; }
    const body = { name: d.name, items, discount_type: d.discount_type, discount_value: parseFloat(d.discount_value), active: d.active, notes: d.notes || null };
    try {
      if (dlg.id) await axios.put(`${API}/companies/${companyId}/factory/combos/${dlg.id}`, body);
      else await axios.post(`${API}/companies/${companyId}/factory/combos`, body);
      toast.success("Combo salvo");
      setDlg({ open: false, id: null, data: null });
      load();
    } catch (e) { toast.error("Erro: " + (e.response?.data?.detail || "")); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir o combo "${c.name}"?`)) return;
    try {
      await axios.delete(`${API}/companies/${companyId}/factory/combos/${c.id}`);
      toast.success("Combo excluído");
      load();
    } catch (e) { toast.error("Erro ao excluir: " + (e.response?.data?.detail || "")); }
  };

  return (
    <div className="space-y-3" data-testid="combos-tab">
      <div className="flex justify-between items-center gap-2">
        <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2 flex-1">
          Monte <b>combos</b> de produtos com desconto (<b>%</b> ou <b>preço fixo</b>). Combos ativos aparecem no <b>Frente de Caixa</b> como uma linha única e baixam o estoque de cada produto.
        </div>
        <Button disabled={!canManage} onClick={() => setDlg({ open: true, id: null, data: { ...EMPTY, items: [{ product_id: "", quantity: "1" }] } })} data-testid="new-combo-btn">
          <Plus className="h-4 w-4 mr-1" />Novo Combo
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {combos.map((c) => <ComboCard key={c.id} c={c} canManage={canManage} onEdit={openEdit} onDelete={remove} />)}
        {combos.length === 0 && <div className="col-span-3 border rounded p-8 text-center text-sm">Nenhum combo cadastrado.</div>}
      </div>

      <Dialog open={dlg.open} onOpenChange={(o) => !o && setDlg({ open: false, id: null, data: null })}>
        <DialogContent className="max-w-lg" data-testid="combo-dialog">
          <DialogHeader><DialogTitle>{dlg.id ? "Editar" : "Novo"} Combo</DialogTitle></DialogHeader>
          {dlg.data && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold">Nome do combo *</label>
                <Input value={dlg.data.name} onChange={(e) => setData({ name: e.target.value })} placeholder="Ex: Kit Obra (AC-I + AC-II)" data-testid="combo-name" />
              </div>
              <ItemsEditor items={dlg.data.items} products={products} onChange={(items) => setData({ items })} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold">Tipo de desconto *</label>
                  <Select value={dlg.data.discount_type} onValueChange={(v) => setData({ discount_type: v })}>
                    <SelectTrigger data-testid="combo-discount-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Percentual (%)</SelectItem>
                      <SelectItem value="fixed">Preço fixo (R$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-bold">{dlg.data.discount_type === "percent" ? "Desconto (%)" : "Preço final do combo (R$)"} *</label>
                  <Input type="number" step="0.01" value={dlg.data.discount_value} onChange={(e) => setData({ discount_value: e.target.value })} data-testid="combo-discount-value" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={dlg.data.active} onChange={(e) => setData({ active: e.target.checked })} data-testid="combo-active" />
                Ativo (disponível no Frente de Caixa)
              </label>
              <div>
                <label className="text-xs font-bold">Observações</label>
                <Input value={dlg.data.notes} onChange={(e) => setData({ notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg({ open: false, id: null, data: null })}>Cancelar</Button>
            <Button onClick={save} data-testid="combo-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
